type SupplierLike = { id: string; supplier_name: string };
type ReferenceLike = { supplier_id: string | null; supplier_name: string | null };
type ProductionDateLike = { supplierId: string };

export type SupplierOrderGroup<R, P> = {
  supplierId: string | null;
  supplierName: string;
  references: R[];
  productionDates: P[];
};

export const UNASSIGNED_SUPPLIER_LABEL = 'Supplier not set';

/**
 * One group per supplier: the listed suppliers first, then any other supplier that has a reference
 * or production date. Rows without a supplier go in a final "Supplier not set" group.
 */
export function groupSupplierOrders<R extends ReferenceLike, P extends ProductionDateLike>(input: {
  suppliers: SupplierLike[];
  listedSupplierIds: string[];
  references: R[];
  productionDates: P[];
}): Array<SupplierOrderGroup<R, P>> {
  const ids: string[] = [];
  const add = (raw: unknown) => {
    const id = String(raw || '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  };
  input.listedSupplierIds.forEach(add);
  input.references.forEach((row) => add(row.supplier_id));
  input.productionDates.forEach((row) => add(row.supplierId));

  const nameOf = (id: string) => (
    input.suppliers.find((row) => row.id === id)?.supplier_name
    || input.references.find((row) => row.supplier_id === id)?.supplier_name
    || 'Unknown supplier'
  );
  const groups: Array<SupplierOrderGroup<R, P>> = ids.map((id) => ({
    supplierId: id,
    supplierName: nameOf(id),
    references: input.references.filter((row) => row.supplier_id === id),
    productionDates: input.productionDates.filter((row) => row.supplierId === id),
  }));

  const unassignedReferences = input.references.filter((row) => !String(row.supplier_id || '').trim());
  const unassignedDates = input.productionDates.filter((row) => !String(row.supplierId || '').trim());
  if (unassignedReferences.length || unassignedDates.length) {
    groups.push({
      supplierId: null,
      supplierName: UNASSIGNED_SUPPLIER_LABEL,
      references: unassignedReferences,
      productionDates: unassignedDates,
    });
  }
  return groups;
}
